'use client';

import React, { type ForwardedRef, forwardRef, useMemo } from 'react';
import { Text } from 'components/Text';
import {
  IconCheckCircleFill,
  IconInfoFill,
  IconWarningFill,
  IconXCircleFill,
} from '@frameio/vapor-icons';
import { theme } from '@frameio/vapor-theme';
import { ToastButtonGroup, ToastCloseButton } from './ToastButton';
import {
  ToastActions,
  ToastAdornmentBeforeContainer,
  ToastDisplayMessages,
  ToastLoadingIndicator,
  ToastMotionBox,
} from './ToastContents.styled';
import { type ToastAlertVariants, type ToastContentsProps } from './ToastTypes';

function ToastAlertContents(
  {
    adornmentAfter,
    onRequestClose,
    variant = 'neutral',
    closeLabel = 'Dismiss',
    title,
    details,
    adornmentBefore,
    isLoading = false,
  }: ToastContentsProps,
  forwardedRef: ForwardedRef<HTMLDivElement>
) {
  const actions = useMemo(() => {
    const hasAdornmentAfter = Boolean(adornmentAfter || onRequestClose);
    if (!hasAdornmentAfter) return null;

    const renderedAdornment =
      typeof adornmentAfter === 'function'
        ? adornmentAfter(onRequestClose)
        : adornmentAfter;

    return <ToastButtonGroup>{renderedAdornment}</ToastButtonGroup>;
  }, [adornmentAfter, onRequestClose]);

  return (
    <ToastMotionBox
      ref={forwardedRef}
      $hasHorizontalBoundaries={true}
      animate={{
        opacity: 1,
      }}
      data-loading={isLoading || undefined}
      exit={{
        opacity: 0,
      }}
      initial={{
        opacity: 0,
      }}
      transition={{ duration: 0.45 }}
    >
      {isLoading && (
        <ToastLoadingIndicator
          data-chromatic="ignore"
          data-testid="toast-alert--loading-indicator"
        />
      )}
      {(variant !== 'neutral' || adornmentBefore) && (
        <ToastAdornmentBeforeContainer>
          {adornmentBefore || <IconForVariant variant={variant} />}
        </ToastAdornmentBeforeContainer>
      )}
      <ToastDisplayMessages>
        <Text
          color="primary"
          data-testid="toast-alert--text-primary"
          fontWeight="medium"
          style={{
            overflowWrap: 'anywhere',
          }}
          variant="body"
        >
          {title}
        </Text>
        {details && (
          <Text
            color="tertiary"
            data-testid="toast-alert--text-secondary"
            variant="body"
          >
            {details}
          </Text>
        )}
      </ToastDisplayMessages>
      <ToastActions>
        {actions}
        {onRequestClose && (
          <ToastCloseButton label={closeLabel} onPress={onRequestClose} />
        )}
      </ToastActions>
    </ToastMotionBox>
  );
}

const _ToastContents = forwardRef(ToastAlertContents);
_ToastContents.displayName = 'ToastContents';
export { _ToastContents as ToastContents };

/**
 * A subcomponent that renders an icon based on the variant
 */
function IconForVariant({ variant }: { variant: ToastAlertVariants }) {
  switch (variant) {
    case 'success':
      return (
        <IconCheckCircleFill color={theme.colors.icon.interactive.success} />
      );

    case 'info':
      return <IconInfoFill color={theme.colors.brand.primary} />;

    case 'warning':
      return <IconWarningFill color={theme.colors.icon.interactive.notice} />;

    case 'error':
      return <IconXCircleFill color={theme.colors.icon.interactive.error} />;

    default:
      return null;
  }
}
